import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import ContentLayout from '../../users/components/ContentLayout';
import TableSection from '../../users/components/TableSection';
import DeleteModal from '../../../components/modals/DeleteModal';
import MedicationModal from '../components/modals/MedicationModal';
import { deleteMedication, fetchMedications } from '../api/medicationsApi';
import { useAuth } from '../../../context/useAuth';
import { PAGE_SIZE } from '../../../config';
import { isStaff } from '../../../shared/utils/roles';

export default function MedicationsPage() {
  const { user } = useAuth();

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [medications, setMedications] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalResults, setTotalResults] = useState(null);
  const [listLoading, setListLoading] = useState(false);
  const [message, setMessage] = useState('');

  const [isMedicationModalOpen, setIsMedicationModalOpen] = useState(false);
  const [selectedMedication, setSelectedMedication] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [medicationToDelete, setMedicationToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteDone, setDeleteDone] = useState(false);
  const latestMedicationsRequestRef = useRef(0);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  const loadMedications = useCallback(async (page = currentPage) => {
    const requestId = latestMedicationsRequestRef.current + 1;
    latestMedicationsRequestRef.current = requestId;
    setListLoading(true);

    const { data, ok, message } = await fetchMedications({
      page,
      limit: PAGE_SIZE,
      search: debouncedSearch,
    });

    if (requestId !== latestMedicationsRequestRef.current) {
      return;
    }

    setListLoading(false);

    if (ok && data) {
      const resolvedPage = data?.pagination?.currentPage || 1;
      setMedications(data.medications || []);
      if (resolvedPage !== page) {
        setCurrentPage(resolvedPage);
      }
      setTotalPages(data?.pagination?.totalPages || 1);
      setTotalResults(data?.pagination?.totalCount ?? null);
    } else if (message) {
      setMessage(message);
    }
  }, [currentPage, debouncedSearch]);

  useEffect(() => {
    loadMedications(currentPage);
  }, [loadMedications]);

  const handleCreateOpen = () => {
    setSelectedMedication(null);
    setIsMedicationModalOpen(true);
  };

  const handleEditOpen = (medication) => {
    setSelectedMedication(medication);
    setIsMedicationModalOpen(true);
  };

  const handleMedicationModalClose = () => {
    setSelectedMedication(null);
    setIsMedicationModalOpen(false);
  };

  const handleDeleteOpen = (medication) => {
    setMedicationToDelete(medication);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteClose = () => {
    setMedicationToDelete(null);
    setDeleteLoading(false);
    setDeleteDone(false);
    setIsDeleteModalOpen(false);
  };

  const handleDeleteMedication = async () => {
    if (!medicationToDelete?._id) return;

    setDeleteLoading(true);
    const response = await deleteMedication(medicationToDelete._id);

    if (!response.ok) {
      setMessage(response.message);
      setDeleteLoading(false);
      return;
    }

    setDeleteDone(true);
    setMessage(response.message || 'Lek obrisan.');
    await loadMedications();

    setTimeout(() => {
      handleDeleteClose();
    }, 700);
  };

  const columns = useMemo(
    () => [
      {
        key: 'redniBroj',
        label: 'Red. broj',
        render: (_, row) => {
          const rowIndex = medications.findIndex((item) => item?._id === row?._id);
          if (rowIndex < 0) return '-';
          return (currentPage - 1) * 10 + rowIndex + 1;
        },
      },
      {
        key: 'name',
        label: 'Naziv leka',
        render: (val) => val || '-',
      },
      {
        key: 'createdAt',
        label: 'Kreiran',
        render: (val) => (val ? dayjs(val).format('DD.MM.YYYY HH:mm') : '-'),
      },
      {
        key: 'updatedAt',
        label: 'Poslednja izmena',
        render: (val) => (val ? dayjs(val).format('DD.MM.YYYY HH:mm') : '-'),
      },
    ],
    [currentPage, medications]
  );

  return (
    <>
      <ContentLayout>
        <TableSection
          showCreateButton={isStaff(user)}
          createButtonDisplay="Dodaj lek"
          onCreateClick={handleCreateOpen}
          onSearch={setSearch}
          columns={columns}
          actions={[
            {
              key: 'edit',
              label: 'Azuriraj lek',
              icon: (
                <img
                  src="/assets/icons/edit-icon.svg"
                  alt="Izmeni"
                  width={16}
                  height={16}
                  className="dropdown-icon"
                />
              ),
              onClick: handleEditOpen,
              visible: () => isStaff(user),
            },
            {
              key: 'delete',
              label: 'Obrisi lek',
              icon: (
                <img
                  src="/assets/icons/delete.svg"
                  alt="Obriši"
                  width={16}
                  height={16}
                  className="dropdown-icon"
                />
              ),
              onClick: handleDeleteOpen,
              visible: () => isStaff(user),
              danger: true,
            },
          ]}
          data={medications}
          emptyState={{
            icon: '/assets/icons/no-users.svg',
            title: 'Trenutno nema lekova',
            description: 'Dodajte lek.',
          }}
          showPagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalResults={totalResults}
          paginationLoading={listLoading}
          onPageChange={setCurrentPage}
          searchPlaceholder="Pretraži lekove"
        />
      </ContentLayout>
      <MedicationModal
        isOpen={isMedicationModalOpen}
        onClose={handleMedicationModalClose}
        onSaved={loadMedications}
        medication={selectedMedication}
      />
      <DeleteModal
        visible={isDeleteModalOpen}
        onDelete={handleDeleteMedication}
        onCancel={handleDeleteClose}
        loading={deleteLoading}
        done={deleteDone}
        title="Brisanje leka"
        message={`Da li ste sigurni da zelite da obrisete lek "${medicationToDelete?.name || ''}"?`}
        buttonText="Obrisi lek"
      />
      {!!message && <span style={{ display: 'none' }}>{message}</span>}
    </>
  );
}
